import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { formatCurrency } from '../CoreFunctions';

/*
Props:
- row: { id, amount, date, description, sourceType: 'cash' | 'revolver' }
- defaultKind: 'recurring' | 'non_recurring' (the category the user initially chose)
- onClose: () => void
- onCompleted: (result) => void  // called after successful split or when user keeps 100%/0%

Behavior:
- Shows total amount (positive magnitude)
- User can enter either recurring amount or percentage; the non-recurring auto-fills to tie out
- Save Split: if both parts > 0.005, call split endpoint, creating two children: { amount, expense_category }
- If 100% kept on defaultKind (i.e., other side is 0), do nothing (no split); just close and call onCompleted
*/

const clamp2 = (n) => {
  const x = Number(n || 0);
  if (!isFinite(x)) return 0;
  return Math.max(0, Math.round(x * 100) / 100);
};

const IncomeSplitModal = ({ row, defaultKind = 'non_recurring', onClose, onCompleted }) => {
  const total = useMemo(() => {
    try {
      if (typeof row.amount === 'object' && row.amount && row.amount.amount != null) {
        return Math.abs(Number(row.amount.amount));
      }
      return Math.abs(Number(row.amount));
    } catch (e) {
      return 0;
    }
  }, [row]);

  const [mode, setMode] = useState('amount'); // 'amount' | 'percent'
  const [recAmount, setRecAmount] = useState(total);
  const [percent, setPercent] = useState(100);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize based on defaultKind: default to 100% for selected kind
  useEffect(() => {
    if (!total) return;
    if (defaultKind === 'recurring') {
      setRecAmount(total);
      setPercent(100);
    } else {
      setRecAmount(0);
      setPercent(0);
    }
  }, [defaultKind, total]);

  const nonRecAmount = useMemo(() => clamp2(total - clamp2(recAmount)), [total, recAmount]);

  const onChangeRecAmount = (v) => {
    const a = clamp2(v);
    const c = Math.min(total, Math.max(0, a));
    setRecAmount(c);
    if (total > 0) setPercent(Math.round((c / total) * 100));
  };

  const onChangePercent = (v) => {
    const p = Math.max(0, Math.min(100, Number(v || 0)));
    setPercent(p);
    setRecAmount(clamp2((p / 100) * total));
  };

  const submit = async () => {
    if (!row || !row.id) { onClose && onClose(); return; }
    setError('');
    const rec = clamp2(recAmount);
    const non = clamp2(total - rec);
    // If no real split (one side is zero), just close
    if (rec <= 0.005 || non <= 0.005) {
      onCompleted && onCompleted({ split: false });
      onClose && onClose();
      return;
    }
    // Build payload with two children in the order: recurring, non-recurring
    const splits = [
      { amount: rec, expense_category: 'income_recurring', description: row.description },
      { amount: non, expense_category: 'income_nonrecurring', description: row.description },
    ];
    setSaving(true);
    try {
      if (row.sourceType === 'cash') {
        await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/split/`, { splits });
      } else {
        await axios.post(`/api/balance_sheet/financing/revolvers/transactions/${row.id}/split/`, { splits });
      }
      onCompleted && onCompleted({ split: true });
      onClose && onClose();
    } catch (e) {
      setError('Failed to split transaction.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="split-modal-backdrop" style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex: 2100 }}>
      <div className="split-modal" style={{ maxWidth: 520, margin:'10vh auto', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb', position:'relative', zIndex: 2101 }}>
        <div className="split-modal-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:12, borderBottom:'1px solid #374151' }}>
          <h5 className="modal-title">Split Income: Recurring vs Non‑recurring</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={saving}>✕</button>
        </div>
        <div className="modal-body" style={{ padding:16 }}>
          <div style={{ marginBottom: 10, color:'#9ca3af' }}>
            <div><strong>Description:</strong> {row?.description || ''}</div>
            <div><strong>Date:</strong> {row?.date || row?.transaction_date || ''}</div>
            <div><strong>Total:</strong> {formatCurrency(total)}</div>
          </div>

          <div className="control-group" style={{ marginBottom: 10 }}>
            <label style={{ display:'block', marginBottom:6 }}>Input mode</label>
            <div style={{ display:'flex', gap:8 }}>
              <label style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input type="radio" checked={mode==='amount'} onChange={()=> setMode('amount')} /> Amount
              </label>
              <label style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input type="radio" checked={mode==='percent'} onChange={()=> setMode('percent')} /> Percentage
              </label>
            </div>
          </div>

          {mode === 'amount' ? (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label className="form-label">Recurring Amount</label>
                <input type="number" step="0.01" className="form-control" value={recAmount}
                       onChange={e => onChangeRecAmount(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Non‑recurring Amount</label>
                <input type="number" step="0.01" className="form-control" value={nonRecAmount} readOnly />
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label className="form-label">Recurring %</label>
                <input type="number" min="0" max="100" step="1" className="form-control" value={percent}
                       onChange={e => onChangePercent(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Non‑recurring %</label>
                <input type="number" className="form-control" value={Math.max(0, 100 - (Number(percent)||0))} readOnly />
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" style={{ marginTop: 10, background:'transparent', color:'#f87171', borderColor:'#7f1d1d' }}>{error}</div>
          )}
          <div style={{ marginTop: 10, color:'#9ca3af' }}>
            Tip: If you keep 100% on one side, no split will be created and the existing category will remain.
          </div>
        </div>
        <div className="modal-footer" style={{ display:'flex', justifyContent:'flex-end', gap:8, padding:12, borderTop:'1px solid #374151' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-success" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save Split'}</button>
        </div>
      </div>
    </div>
  );
};

export default IncomeSplitModal;
